/**
 * chatPhase — coarse "where is the learner in the chat flow?" signal that
 * ChatPage publishes on `<html data-chat-phase>` and the global <HomeButton>
 * (Issue #1555) consumes.
 *
 * Why a DOM-attribute bridge rather than props/context: the home FAB is a
 * GLOBAL floating affordance mounted once in App.jsx (sibling to
 * <BugReporter />), but the show/hide + confirm-on-leave decision depends on
 * ChatPage-internal state (run status, which pre-chat overlay is up). Routing
 * the single derived phase string through `document.documentElement` keeps the
 * button fully self-contained (no route allowlist, no new provider) and
 * mirrors how BugReporter already publishes `--bug-reporter-width`. ChatPage
 * owns the attribute and clears it on unmount, so the FAB never lingers on
 * non-chat screens.
 *
 * This module is deliberately React-free so ChatPage, HomeButton, and
 * node-env vitest can all import it without dragging in contexts or CSS.
 */

export const CHAT_PHASE_ATTR = 'data-chat-phase';

export const CHAT_PHASE = {
  /** A pre-chat / mid-flow modal owns the screen (briefing, mode-select,
   *  camera pick, voice connecting, pre-scenario intro) → hide the FAB. */
  OVERLAY: 'overlay',
  /** Conversation in progress — leaving abandons unsaved work → show + confirm. */
  ACTIVE: 'active',
  /** Debrief / completed run → show, no confirm (nothing left to lose). */
  COMPLETE: 'complete',
};

/**
 * Derive the chat phase from ChatPage's existing state. Returns null when the
 * learner is not on a live scenario surface (no run, loading, error) so the
 * caller removes the attribute entirely.
 *
 * The overlay rules mirror the ChatPage visibility matrix
 * (frontend/src/constants/chatPageVisibility.js): the IntroModal is up while
 * `selectedMode === null` (briefing / mode-select) or while voice is still
 * connecting (`selectedMode === 'voice' && loadingState !== 'idle'`), and the
 * camera/pre-scenario-intro modals are explicit booleans.
 *
 * @param {object} ctx
 * @param {('active'|'completed'|null)} ctx.runStatus
 * @param {(string|null)} ctx.selectedMode      'text' | 'voice' | null
 * @param {string} ctx.loadingState             voice connection state ('idle' when not connecting)
 * @param {boolean} ctx.showPreScenarioIntro    Issue #1095 top-level intro overlay
 * @param {boolean} ctx.showCameraSelection     Issue #149 camera-pick modal
 * @returns {('overlay'|'active'|'complete'|null)}
 */
export function deriveChatPhase({
  runStatus,
  selectedMode,
  loadingState,
  showPreScenarioIntro,
  showCameraSelection,
} = {}) {
  // Only an active or completed run is a learner conversation surface.
  if (runStatus !== 'active' && runStatus !== 'completed') return null;

  // Explicit full-screen modals always win — never float the FAB over them.
  if (showPreScenarioIntro || showCameraSelection) return CHAT_PHASE.OVERLAY;

  if (runStatus === 'completed') return CHAT_PHASE.COMPLETE;

  // runStatus === 'active' below.
  // Briefing / mode-select: no mode confirmed yet.
  if (selectedMode == null) return CHAT_PHASE.OVERLAY;
  // Voice picked but still connecting (warming mic / dialing).
  if (selectedMode === 'voice' && loadingState !== 'idle') return CHAT_PHASE.OVERLAY;

  return CHAT_PHASE.ACTIVE;
}

/** Whether the home FAB should be rendered for the given phase. */
export function isHomeAffordanceVisible(phase) {
  return phase === CHAT_PHASE.ACTIVE || phase === CHAT_PHASE.COMPLETE;
}

/** Whether leaving for home should prompt a confirm (active run = unsaved work). */
export function requiresLeaveConfirm(phase) {
  return phase === CHAT_PHASE.ACTIVE;
}
